import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase, FeedbackRequest } from '../lib/supabase';
import { anonymousSupabase, getSessionDiagnostics, hasAuthenticatedSession } from '../lib/anonymous-client';
import { uploadAudioFile } from '../lib/storage-utils';
import { Mic, Square, Play, Pause, Loader2, CheckCircle2, AlertCircle, Home } from 'lucide-react';
import BackToHome from './BackToHome';

export default function FeedbackForm() {
  const { linkId } = useParams<{ linkId: string }>();
  const navigate = useNavigate();
  const [request, setRequest] = useState<FeedbackRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorDetails, setErrorDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [duration, setDuration] = useState(0);
  const [textFeedback, setTextFeedback] = useState('');

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    loadRequest();
    logSessionDiagnostics();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [linkId]);

  const logSessionDiagnostics = async () => {
    try {
      const diagnostics = await getSessionDiagnostics();
      const hasAuth = await hasAuthenticatedSession();

      console.log('[FeedbackForm] Session Diagnostics:', {
        hasAuthSession: hasAuth,
        storageKeys: diagnostics.storageKeys,
        sessionCount: diagnostics.sessionCount,
        timestamp: new Date().toISOString()
      });

      if (hasAuth) {
        console.warn('[FeedbackForm] ⚠️ Authenticated session detected - this may interfere with anonymous feedback submission');
        console.warn('[FeedbackForm] The app will use anonymous client to bypass this issue');
      }
    } catch (err) {
      console.error('[FeedbackForm] Failed to log session diagnostics:', err);
    }
  };

  const loadRequest = async () => {
    // Generate unique request ID for logging
    const requestId = `load-${Date.now()}-${Math.random().toString(36).substring(7)}`;

    console.log(`[${requestId}] Loading feedback request for linkId:`, linkId);
    console.log(`[${requestId}] Using anonymous Supabase client for feedback operations`);

    if (!linkId) {
      console.error(`[${requestId}] No linkId provided in URL`);
      setError('Invalid feedback request link');
      setErrorDetails('The link you followed is missing the required identifier.');
      setLoading(false);
      return;
    }

    // Validate linkId format (should be alphanumeric, 20-30 chars)
    if (!/^[a-z0-9]{15,30}$/i.test(linkId)) {
      console.error(`[${requestId}] Invalid linkId format:`, linkId);
      setError('Invalid feedback request link format');
      setErrorDetails(`The link format appears to be incorrect. Expected alphanumeric string, got: ${linkId.substring(0, 20)}...`);
      setLoading(false);
      return;
    }

    try {
      console.log(`[${requestId}] Querying database for unique_link:`, linkId);

      // Use anonymous client to avoid auth session interference
      const { data, error: queryError } = await anonymousSupabase
        .from('feedback_requests')
        .select('*')
        .eq('unique_link', linkId)
        .maybeSingle();

      // Log detailed error information
      if (queryError) {
        console.error(`[${requestId}] Database query error:`, {
          code: queryError.code,
          message: queryError.message,
          details: queryError.details,
          hint: queryError.hint,
        });
        throw queryError;
      }

      console.log(`[${requestId}] Query completed. Data found:`, !!data);

      if (!data) {
        console.error(`[${requestId}] No feedback request found for linkId:`, linkId);
        setError('Feedback request not found');
        setErrorDetails(
          'This feedback request does not exist or may have been deleted. ' +
          'Please check your email for the correct link or contact the person who sent the request.'
        );
        setLoading(false);
        return;
      }

      console.log(`[${requestId}] Request found. Status:`, data.status, 'Expires:', data.expires_at);

      // Check if already completed
      if (data.status === 'completed') {
        console.warn(`[${requestId}] Request already completed at:`, data.completed_at);
        setError('This feedback has already been submitted');
        setErrorDetails(
          `This feedback was submitted on ${new Date(data.completed_at || '').toLocaleDateString()}. ` +
          'Thank you for your participation!'
        );
        setLoading(false);
        return;
      }

      // Check expiration
      const expiresAt = new Date(data.expires_at);
      const now = new Date();

      if (expiresAt < now) {
        const daysExpired = Math.floor((now.getTime() - expiresAt.getTime()) / (1000 * 60 * 60 * 24));
        console.warn(`[${requestId}] Request expired ${daysExpired} days ago on:`, data.expires_at);
        setError('This feedback request has expired');
        setErrorDetails(
          `This request expired on ${expiresAt.toLocaleDateString()}. ` +
          'Please contact the requester if you still want to provide feedback.'
        );
        setLoading(false);
        return;
      }

      console.log(`[${requestId}] Request loaded successfully for recipient:`, data.recipient_email);
      setRequest(data);
      setError('');
      setErrorDetails('');

    } catch (err: any) {
      console.error(`[${requestId}] Unexpected error loading request:`, err);
      console.error(`[${requestId}] Error stack:`, err.stack);

      // Provide specific error messages based on error type
      let userMessage = 'Failed to load feedback request';
      let details = err.message || 'An unknown error occurred';

      if (err.code === 'PGRST116') {
        userMessage = 'Feedback request not found';
        details = 'The database query returned no results. The link may be invalid or the request may have been deleted.';
      } else if (err.message?.includes('Failed to fetch')) {
        userMessage = 'Network connection error';
        details = 'Could not connect to the server. Please check your internet connection and try again.';
      } else if (err.code === '42P01') {
        userMessage = 'Database configuration error';
        details = 'The feedback_requests table does not exist. Please contact support.';
      } else if (err.message?.includes('JWT')) {
        userMessage = 'Authentication error';
        details = 'There was an authentication issue. This should not affect anonymous feedback. Please try again.';
      }

      setError(userMessage);
      setErrorDetails(details);

    } finally {
      setLoading(false);
    }
  };

  const startRecording = async () => {
    const recordId = `record-${Date.now()}-${Math.random().toString(36).substring(7)}`;
    console.log(`[${recordId}] Starting audio recording...`);

    try {
      console.log(`[${recordId}] Requesting microphone access...`);
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      console.log(`[${recordId}] Microphone access granted`);
      console.log(`[${recordId}] Audio tracks: ${stream.getAudioTracks().length}`);

      const mediaRecorder = new MediaRecorder(stream);
      console.log(`[${recordId}] MediaRecorder created with mimeType: ${mediaRecorder.mimeType}`);

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
          console.log(`[${recordId}] Audio chunk received: ${event.data.size} bytes`);
        }
      };

      mediaRecorder.onstop = () => {
        console.log(`[${recordId}] Recording stopped. Total chunks: ${audioChunksRef.current.length}`);
        const totalSize = audioChunksRef.current.reduce((sum, chunk) => sum + chunk.size, 0);
        console.log(`[${recordId}] Total audio size: ${totalSize} bytes (${(totalSize / 1024 / 1024).toFixed(2)} MB)`);

        if (audioChunksRef.current.length === 0) {
          console.error(`[${recordId}] ❌ No audio chunks recorded!`);
          setError('Recording failed - no audio data captured. Please try again.');
          stream.getTracks().forEach(track => track.stop());
          return;
        }

        if (totalSize === 0) {
          console.error(`[${recordId}] ❌ Audio chunks have 0 bytes!`);
          setError('Recording failed - empty audio data. Please try again.');
          stream.getTracks().forEach(track => track.stop());
          return;
        }

        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        console.log(`[${recordId}] ✅ Blob created successfully:`, {
          size: blob.size,
          type: blob.type
        });

        setAudioBlob(blob);
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        stream.getTracks().forEach(track => {
          track.stop();
          console.log(`[${recordId}] Audio track stopped`);
        });
      };

      mediaRecorder.onerror = (event: any) => {
        console.error(`[${recordId}] MediaRecorder error:`, event.error);
        setError(`Recording error: ${event.error?.message || 'Unknown error'}`);
      };

      // Request data every 100ms to ensure we capture audio
      // This prevents issues with browsers that don't emit ondataavailable until stop
      mediaRecorder.start(100);
      console.log(`[${recordId}] Recording started with 100ms timeslice`);
      setIsRecording(true);
      setIsPaused(false);

      timerRef.current = window.setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
    } catch (err: any) {
      console.error(`[${recordId}] Microphone access error:`, err);

      let errorMessage = 'Could not access microphone';
      if (err.name === 'NotAllowedError') {
        errorMessage = 'Microphone access denied. Please allow microphone permissions in your browser settings.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'No microphone found. Please connect a microphone and try again.';
      } else if (err.name === 'NotReadableError') {
        errorMessage = 'Microphone is already in use by another application.';
      }

      setError(errorMessage);
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current?.state === 'paused') {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      timerRef.current = window.setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const submitId = `submit-${Date.now()}-${Math.random().toString(36).substring(7)}`;
    console.log(`[${submitId}] Starting feedback submission for request:`, request?.id);

    if (!request || (!audioBlob && !textFeedback.trim())) {
      setError('Please provide feedback either by recording or typing');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      let uploadedAudioUrl = null;

      // Upload audio if provided
      if (audioBlob) {
        console.log(`[${submitId}] === AUDIO UPLOAD START ===`);
        console.log(`[${submitId}] Audio blob details:`, {
          size: audioBlob.size,
          type: audioBlob.type,
          duration: duration,
          requestId: request.id
        });

        // BUG-H002 FIX: File size validation before upload
        const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB limit
        if (audioBlob.size > MAX_FILE_SIZE) {
          const sizeMB = (audioBlob.size / 1024 / 1024).toFixed(2);
          console.error(`[${submitId}] ❌ Audio file too large: ${sizeMB}MB`);
          setError(`Audio file is too large (${sizeMB}MB). Maximum size is 10MB. Please record a shorter message.`);
          setSubmitting(false);
          return;
        }

        // Pre-upload validation
        if (audioBlob.size === 0) {
          console.error(`[${submitId}] ❌ Audio blob is empty (0 bytes)`);
          throw new Error('Recording is empty. Please record audio before submitting.');
        }

        if (audioBlob.size < 100) {
          console.warn(`[${submitId}] ⚠️  Audio blob is very small (${audioBlob.size} bytes) - may be corrupted`);
        }

        console.log(`[${submitId}] Starting upload to storage...`);

        // Upload directly - the upload function handles all validation and errors
        const { url, error: uploadError } = await uploadAudioFile(audioBlob, request.id);

        if (uploadError || !url) {
          console.error(`[${submitId}] Audio upload failed:`, uploadError);
          throw new Error(uploadError || 'Failed to upload audio file');
        }

        uploadedAudioUrl = url;
        console.log(`[${submitId}] Audio uploaded successfully to: ${url}`);
      } else {
        console.log(`[${submitId}] No audio recording - submitting text-only feedback`);
        console.log(`[${submitId}] Text feedback length: ${textFeedback.trim().length} characters`);
      }

      // Insert feedback response
      // Note: A database trigger will automatically update the feedback_request status to 'completed'
      console.log(`[${submitId}] Inserting feedback response...`);

      const insertPayload = {
        request_id: request.id,
        audio_url: uploadedAudioUrl,
        raw_feedback: textFeedback || null,
        duration_seconds: duration,
      };

      // DIAGNOSTIC LOGGING - BEFORE INSERT
      console.log(`[${submitId}] [FEEDBACK_RESPONSE_INSERT] === START ===`);
      console.log(`[${submitId}] [FEEDBACK_RESPONSE_INSERT] Payload:`, JSON.stringify(insertPayload, null, 2));
      console.log(`[${submitId}] [FEEDBACK_RESPONSE_INSERT] Request ID:`, request.id);
      console.log(`[${submitId}] [FEEDBACK_RESPONSE_INSERT] Request status:`, request.status);
      console.log(`[${submitId}] [FEEDBACK_RESPONSE_INSERT] Request expires_at:`, request.expires_at);
      console.log(`[${submitId}] [FEEDBACK_RESPONSE_INSERT] Is expired:`, new Date(request.expires_at) < new Date());

      // Check both default and anonymous client session state
      const { data: defaultSessionData } = await supabase.auth.getSession();
      const { data: anonSessionData } = await anonymousSupabase.auth.getSession();

      console.log(`[${submitId}] [FEEDBACK_RESPONSE_INSERT] Default client session:`, defaultSessionData?.session?.role || 'ANON');
      console.log(`[${submitId}] [FEEDBACK_RESPONSE_INSERT] Anonymous client session:`, anonSessionData?.session?.role || 'ANON');
      console.log(`[${submitId}] [FEEDBACK_RESPONSE_INSERT] Using anonymous client for submission`);

      // Use SECURITY DEFINER function with anonymous client to ensure true anonymous submission
      const { data: insertData, error: insertError } = await anonymousSupabase
        .rpc('submit_feedback_response', {
          p_request_id: request.id,
          p_audio_url: uploadedAudioUrl,
          p_raw_feedback: textFeedback || null,
          p_duration_seconds: duration,
        });

      // DIAGNOSTIC LOGGING - AFTER INSERT
      console.log(`[${submitId}] [FEEDBACK_RESPONSE_INSERT] Insert error:`, insertError);
      console.log(`[${submitId}] [FEEDBACK_RESPONSE_INSERT] Insert data:`, insertData);
      console.log(`[${submitId}] [FEEDBACK_RESPONSE_INSERT] === END ===`);

      if (insertError) {
        console.error(`[${submitId}] ❌ FEEDBACK_RESPONSE INSERT FAILED:`, {
          code: insertError.code,
          message: insertError.message,
          details: insertError.details,
          hint: insertError.hint,
        });
        throw new Error(insertError.message || 'Failed to save feedback response');
      }

      if (!insertData) {
        console.error(`[${submitId}] ❌ NO DATA RETURNED - RLS POLICY REJECTION`);
        throw new Error('Failed to save feedback response. Please ensure the feedback link is still valid.');
      }

      // insertData is now just the UUID from the function
      const responseId = insertData;
      console.log(`[${submitId}] Feedback response inserted successfully:`, responseId);
      console.log(`[${submitId}] Database trigger will update request status automatically`);

      // If audio was uploaded, trigger transcription immediately
      // IMPORTANT: We AWAIT this to ensure it starts before user navigates away
      if (uploadedAudioUrl) {
        console.log(`[${submitId}] ========== TRANSCRIPTION TRIGGER START ==========`);
        console.log(`[${submitId}] Triggering audio transcription for response:`, responseId);
        console.log(`[${submitId}] Audio URL:`, uploadedAudioUrl);

        // Update UI to show we're transcribing
        setSubmitting(true);

        try {
          const transcriptionResponse = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/transcribe-audio`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                responseId: responseId,
                audioUrl: uploadedAudioUrl,
              }),
            }
          );

          console.log(`[${submitId}] Transcription response status:`, transcriptionResponse.status);

          if (transcriptionResponse.ok) {
            const data = await transcriptionResponse.json();
            console.log(`[${submitId}] ✅ Transcription completed:`, {
              transcriptionLength: data.transcription?.length || 0,
              duration: data.duration,
            });
          } else {
            const errorData = await transcriptionResponse.json().catch(() => ({
              error: 'Unknown error',
              errorType: 'PARSE_ERROR'
            }));

            console.error(`[${submitId}] ❌ Transcription failed:`, errorData.error);
            console.error(`[${submitId}] Error type:`, errorData.errorType);
            console.error(`[${submitId}] NOTE: Feedback saved successfully, but transcription failed`);

            // Trigger backup processor to retry
            fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-pending-transcriptions`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                'Content-Type': 'application/json',
              },
            }).catch(() => console.log(`[${submitId}] Backup processor fallback failed`));
          }
        } catch (error: any) {
          console.error(`[${submitId}] ❌ Transcription network error:`, error.message);
          console.error(`[${submitId}] NOTE: Feedback saved, triggering backup processor`);

          // Trigger backup processor
          try {
            await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-pending-transcriptions`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                'Content-Type': 'application/json',
              },
            });
            console.log(`[${submitId}] Backup processor triggered`);
          } catch {
            console.error(`[${submitId}] Backup processor also failed`);
          }
        }

        console.log(`[${submitId}] ========== TRANSCRIPTION TRIGGER END ==========`);
      }

      setSubmitted(true);

    } catch (err: any) {
      console.error(`[${submitId}] Submission error:`, err);
      setError(err.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#8CAFA8] animate-spin mx-auto mb-4" />
          <p className="text-[#6B7280]">Loading feedback request...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !request) {
    const isAlreadySubmitted = error === 'This feedback has already been submitted';

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-[24px] shadow-[0_4px_24px_rgba(0,0,0,0.06)] p-8 max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-[#1F2937] mb-2 text-center">Unable to Load Request</h2>
          <p className="text-red-600 font-semibold text-center mb-3">{error}</p>
          {errorDetails && (
            <p className="text-[#6B7280] text-sm text-center mb-6">{errorDetails}</p>
          )}
          {!isAlreadySubmitted && (
            <div className="bg-[#F0F4F3] border border-blue-200 rounded-lg p-4 text-sm text-[#4B5563] mb-6">
              <p className="font-semibold mb-2">Troubleshooting tips:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Check that you used the complete link from your email</li>
                <li>Make sure the link hasn't expired (valid for 30 days)</li>
                <li>Try refreshing the page</li>
                <li>Contact the person who requested your feedback</li>
              </ul>
            </div>
          )}
          <button
            onClick={() => window.location.href = '/'}
            className="w-full bg-[#8CAFA8] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#7A9B94] transition flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-[#8CAFA8] focus:ring-offset-2"
            aria-label="Go to home page"
          >
            <Home className="w-5 h-5" aria-hidden="true" />
            Go to Home Page
          </button>
        </div>
      </div>
    );
  }

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-[24px] shadow-[0_4px_24px_rgba(0,0,0,0.06)] p-8 max-w-md text-center">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-[#1F2937] mb-2">Thank You!</h2>
          <p className="text-[#6B7280] mb-6">
            Your feedback has been submitted successfully. Your insights will help
            with professional development.
          </p>
          <button
            onClick={() => window.location.href = '/'}
            className="bg-[#8CAFA8] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#7A9B94] transition"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  // Main feedback form
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-12 px-6">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-[24px] shadow-[0_4px_24px_rgba(0,0,0,0.06)] overflow-hidden">
          <div className="bg-gradient-to-r from-[#8CAFA8] to-[#7A9B94] text-white p-8">
            <h1 className="text-3xl font-semibold mb-2">Share Your Feedback</h1>
            <p className="text-blue-100">
              for {request?.recipient_name || request?.recipient_email || 'a colleague'}
            </p>
          </div>

          <div className="p-8">
            {request?.message && (
              <div className="bg-[#F0F4F3] border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-[#6B7280] mb-1 font-semibold">Personal message:</p>
                <p className="text-[#4B5563] italic">"{request.message}"</p>
              </div>
            )}

            {request?.custom_questions && request.custom_questions.length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-[#6B7280] mb-3 font-semibold">Please address these questions in your feedback:</p>
                <ol className="space-y-2 list-decimal list-inside">
                  {request.custom_questions.map((question, index) => (
                    <li key={index} className="text-[#4B5563]">{question}</li>
                  ))}
                </ol>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-[#1F2937] mb-3">
                  Option 1: Record Audio Feedback (Recommended)
                </h3>
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  {!isRecording && !audioUrl && (
                    <button
                      type="button"
                      onClick={startRecording}
                      className="w-full bg-red-500 text-white py-4 rounded-lg font-semibold hover:bg-red-600 transition flex items-center justify-center gap-2"
                    >
                      <Mic className="w-5 h-5" />
                      Start Recording
                    </button>
                  )}

                  {isRecording && (
                    <div className="space-y-4">
                      <div className="text-center">
                        <div className="text-4xl font-semibold text-red-500 mb-2">
                          {formatDuration(duration)}
                        </div>
                        <p className="text-[#6B7280]">Recording in progress...</p>
                      </div>

                      <div className="flex gap-3">
                        {!isPaused ? (
                          <button
                            type="button"
                            onClick={pauseRecording}
                            className="flex-1 bg-yellow-500 text-white py-3 rounded-lg font-semibold hover:bg-yellow-600 transition flex items-center justify-center gap-2"
                          >
                            <Pause className="w-5 h-5" />
                            Pause
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={resumeRecording}
                            className="flex-1 bg-green-500 text-white py-3 rounded-lg font-semibold hover:bg-green-600 transition flex items-center justify-center gap-2"
                          >
                            <Play className="w-5 h-5" />
                            Resume
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={stopRecording}
                          className="flex-1 bg-gray-700 text-white py-3 rounded-lg font-semibold hover:bg-gray-800 transition flex items-center justify-center gap-2"
                        >
                          <Square className="w-5 h-5" />
                          Stop
                        </button>
                      </div>
                    </div>
                  )}

                  {audioUrl && (
                    <div className="space-y-4">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <CheckCircle2 className="w-6 h-6 text-green-600 inline-block mr-2" />
                        <span className="text-green-700 font-semibold">
                          Recording complete: {formatDuration(duration)}
                        </span>
                      </div>
                      <audio src={audioUrl} controls className="w-full" />
                      <button
                        type="button"
                        onClick={() => {
                          setAudioBlob(null);
                          setAudioUrl(null);
                          setDuration(0);
                        }}
                        className="text-sm text-red-600 hover:text-red-700 font-semibold"
                      >
                        Re-record
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-[#1F2937] mb-3">
                  Option 2: Type Your Feedback
                </h3>
                <textarea
                  value={textFeedback}
                  onChange={(e) => setTextFeedback(e.target.value)}
                  className="w-full h-48 px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8CAFA8] focus:border-transparent transition resize-none"
                  placeholder="Share your feedback here..."
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-700">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || (!audioBlob && !textFeedback.trim())}
                className="w-full bg-[#8CAFA8] text-white py-4 rounded-lg font-semibold hover:bg-[#7A9B94] disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {audioBlob ? 'Transcribing audio...' : 'Submitting...'}
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-5 h-5" />
                    Submit Feedback
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
